/**
 * Start guide — FR-19 advisory AI on deterministic rails.
 *
 * The deterministic matcher (navigator catalog matchCatalog) decides eligibility
 * and is the complete behavior when AI is off. The LLM only (a) picks among
 * eligible intents with a one-sentence why and (b) extracts what the person
 * already said — a title, the options they named — into the preset. The
 * recommendation is always advisory: the human accepts it; nothing navigates
 * or creates on its own (MR-5/MR-6, P-AGENT-1).
 *
 * Pattern reference: src/lib/llmToolAdvisorService.ts. Capability:
 * `start_intent_guide` in src/lib/ai/aiCapabilityRegistry.ts.
 */

import { callFeatureLlm } from '@/lib/llmClient';
import { isAiCapabilityEnabled } from '@/lib/ai/aiAssistSettings';
import { START_POINTS, matchCatalog, type StartEntry } from '@/lib/navigator/catalog';
import type { StartExtraction } from './launchStartPoint';
import { DEFAULT_MODEL } from '../../../supabase/functions/_shared/modelRequest.ts';

export interface StartRecommendation {
  intent: StartEntry;
  /** One sentence addressed to the person. Null on the deterministic path. */
  why: string | null;
  extraction: StartExtraction | null;
  source: 'deterministic' | 'ai';
}

/** Instant, local, always available. Top deterministic match or null. */
export function recommendDeterministic(text: string): StartRecommendation | null {
  const [top] = matchCatalog(text, START_POINTS);
  if (!top) return null;
  return { intent: top.entry, why: null, extraction: null, source: 'deterministic' };
}

const SYSTEM_PROMPT =
  'You help people start in the right place in a prioritization product. ' +
  'You pick exactly one starting point from a fixed list and extract only what the person explicitly said. ' +
  'Respond with valid JSON only.';

function buildGuidePrompt(text: string, candidates: StartEntry[]): string {
  const lines = candidates.map(
    (i) => `- ${i.id}: "${i.title}" — ${i.summary}`
  );
  return `A person described what they are trying to do:
"${text.slice(0, 600)}"

Starting points to choose from (use the id exactly as written):
${lines.join('\n')}

Pick the single best starting point for their situation. Then extract only what they actually said:
- suggestedTitle: a short working title for this effort (e.g. "CRM vendor decision"), or null if nothing is inferable
- options: the specific options/candidates/topics they explicitly named, as short strings, or null if they named none. Never invent options; a count alone ("four vendors") is not a list of options.

Write "why" as one short sentence addressed to the person explaining the fit.

Return JSON only, no markdown or extra text:
{"intentId": "...", "why": "...", "suggestedTitle": "..." , "options": ["..."]}`;
}

function parseGuideResponse(
  content: string,
  candidates: StartEntry[]
): StartRecommendation | null {
  const jsonMatch = content.match(/\{[\s\S]*\}/);
  if (!jsonMatch) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(jsonMatch[0]);
  } catch {
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null) return null;
  const obj = parsed as Record<string, unknown>;

  // Rails: the model may only pick an id that was in its candidate list.
  const intent = candidates.find((i) => i.id === obj.intentId);
  if (!intent) return null;

  const why =
    typeof obj.why === 'string' && obj.why.trim()
      ? obj.why.trim().slice(0, 240)
      : null;

  const suggestedTitle =
    typeof obj.suggestedTitle === 'string' && obj.suggestedTitle.trim()
      ? obj.suggestedTitle.trim().slice(0, 80)
      : undefined;

  const options = Array.isArray(obj.options)
    ? obj.options
        .filter((o): o is string => typeof o === 'string' && o.trim().length > 0)
        .map((o) => o.trim().slice(0, 120))
        .slice(0, 12)
    : undefined;

  const extraction: StartExtraction | null =
    suggestedTitle || (options && options.length > 0)
      ? { suggestedTitle, options }
      : null;

  return { intent, why, extraction, source: 'ai' };
}

/**
 * LLM refinement of the deterministic recommendation. Returns null whenever
 * the capability is off, the transport fails, or the response is malformed —
 * callers keep the deterministic result in that case. Never throws.
 */
export async function refineRecommendation(
  text: string
): Promise<StartRecommendation | null> {
  const trimmed = text.trim();
  if (!trimmed || !isAiCapabilityEnabled('start_intent_guide')) return null;

  // Rails: candidates are the deterministic matches; when nothing matched,
  // the whole (small, fixed) catalog is the candidate space. Output is
  // validated against this list either way.
  const matches = matchCatalog(trimmed, START_POINTS);
  const candidates =
    matches.length > 0 ? matches.map((m) => m.entry) : START_POINTS;

  const content = await callFeatureLlm({
    userPrompt: buildGuidePrompt(trimmed, candidates),
    systemPrompt: SYSTEM_PROMPT,
    conversationId: `start-guide-${Date.now()}`,
    model: DEFAULT_MODEL,
    temperature: 0.3,
    maxTokens: 400,
  });
  if (!content) return null;

  return parseGuideResponse(content, candidates);
}
